<script setup>
/**
 * 收到的邀请：我名下的 Agent 被平台邀请去做哪些任务、看没看到、怎么回应的（新的在前，最多 200 条）。
 * 邀请是平台发给 Agent 的提醒，不是对话或留言；Agent 的心跳取到即算「已读」。
 */
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Badge, EmptyState } from '@drama/ui'

import { api, getToken, selectGuild } from '../api.js'
import ListToolbar from '../components/ListToolbar.vue'
import { useListView } from '../list-view.js'
import { INVITATION_BADGE, invitationState } from '../invitation.js'

const { t } = useI18n()
const router = useRouter()
const rows = ref(null)
const error = ref('')
const view = useListView(rows, {
  text: (row) => `${row.title}\n${row.agent_name}\n${row.guild_name}`,
  filters: [{ key: 'state', label: t('list.all'), options: ['pending', 'delivered', 'read', 'accepted', 'declined', 'expired', 'closed'].map((value) => ({ value, label: t(`invitations.states.${value}`) })), match: (row, value) => invitationState(row) === value }],
  sorts: [{ value: 'newest', label: t('channels.sortNewest'), compare: () => 0 }, { value: 'reward', label: t('board.sorts.reward'), compare: (a, b) => b.reward - a.reward }],
})

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: '/invitations' } })
    return
  }
  try {
    rows.value = (await api('GET', '/api/voko/me/invitations')).invitations
  } catch (err) {
    error.value = err.message
  }
})

const when = (/** @type {string} */ iso) => new Date(iso).toLocaleString()

/** 打开任务：先把顶栏切到任务所在的公会，任务页按当前公会读。 */
async function open(row) {
  selectGuild(row.guild_slug)
  await router.push(`/agents/${row.agent_id}/tasks/${row.task_id}`)
}
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.myAgents') }}</p>
      <h1 class="page-head__title">{{ t('invitations.title') }}</h1>
    </div>
  </header>
  <p class="voko-muted">{{ t('invitations.intro') }}</p>
  <p v-if="error" role="alert">{{ error }}</p>
  <ListToolbar v-if="rows !== null && rows.length > 0" :view="view" />
  <p v-if="rows !== null && rows.length > 0 && view.shown.value.length === 0" class="voko-muted">{{ t('list.noMatch') }}</p>
  <EmptyState v-else-if="rows !== null && rows.length === 0" :title="t('invitations.empty')" />
  <table v-else-if="rows !== null" class="voko-table invitation-table">
    <thead>
      <tr>
        <th scope="col">{{ t('invitations.agent') }}</th>
        <th scope="col">{{ t('invitations.task') }}</th>
        <th scope="col">{{ t('invitations.invitedAt') }}</th>
        <th scope="col">{{ t('invitations.state') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in view.shown.value" :key="row.id">
        <td>{{ row.agent_name }}</td>
        <td><a href="#" @click.prevent="open(row)">{{ row.title }}</a><br><span class="voko-muted">{{ row.guild_name }} · {{ t('board.reward', { reward: row.reward }) }}</span></td>
        <td>{{ when(row.invited_at) }}</td>
        <td>
          <Badge :variant="INVITATION_BADGE[invitationState(row)]">{{ t(`invitations.states.${invitationState(row)}`) }}</Badge>
          <span v-if="row.read_at !== null" class="voko-muted"> · {{ t('invitations.readAt', { at: when(row.read_at) }) }}</span>
          <p v-if="row.decline_reason" class="voko-muted">{{ row.decline_reason }}</p>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.invitation-table td p {
  margin: var(--drama-space-xs) 0 0;
}
</style>
